/**
 * Contexto de telemetría UDV por sesión (tenant + URL ingest).
 * Prioridad: ticket/plataforma → query URL → manifest prefix map → env build-time.
 */

/** @type {{ tenantId?: string, telemetryBaseUrl?: string, telemetryToken?: string, source?: string } | null} */
let session = null

/**
 * @param {{ tenantId?: string|null, telemetryBaseUrl?: string|null, telemetryToken?: string|null, source?: string } | null} ctx
 */
export function setUdvTelemetrySession(ctx) {
  if (!ctx || typeof ctx !== 'object') {
    session = null
    return
  }
  const tenantId =
    ctx.tenantId != null && String(ctx.tenantId).trim() !== ''
      ? String(ctx.tenantId).trim()
      : undefined
  const telemetryBaseUrl =
    ctx.telemetryBaseUrl != null && String(ctx.telemetryBaseUrl).trim() !== ''
      ? String(ctx.telemetryBaseUrl).trim().replace(/\/+$/, '')
      : undefined
  const telemetryToken =
    ctx.telemetryToken != null && String(ctx.telemetryToken).trim() !== ''
      ? String(ctx.telemetryToken).trim()
      : undefined
  session = {
    tenantId,
    telemetryBaseUrl,
    telemetryToken,
    source: ctx.source || 'session',
  }
}

export function clearUdvTelemetrySession() {
  session = null
}

/** @returns {{ tenantId?: string, telemetryBaseUrl?: string, telemetryToken?: string, source?: string } | null} */
export function getUdvTelemetrySession() {
  return session ? { ...session } : null
}

/**
 * @param {object} ticket normalizeTicketResponse
 */
export function setUdvTelemetrySessionFromTicket(ticket) {
  if (!ticket || typeof ticket !== 'object') return
  setUdvTelemetrySession({
    tenantId: ticket.tenantId,
    telemetryBaseUrl: ticket.telemetryBaseUrl,
    telemetryToken: ticket.telemetryToken,
    source: 'ticket',
  })
}

function parsePrefixTenantMap() {
  try {
    const raw = process.env.REACT_APP_UDV_TELEMETRY_PREFIX_MAP
    if (!raw || typeof raw !== 'string') return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch (_) {
    return null
  }
}

/**
 * Inferir tenant desde path R2: …/{zipKeyPrefix}/{StudyUID}/manifest.json
 * @param {string|null|undefined} manifestUrl
 * @returns {string}
 */
export function tenantIdFromManifestUrl(manifestUrl) {
  const raw = manifestUrl != null ? String(manifestUrl).trim() : ''
  if (!raw) return ''
  try {
    const parts = new URL(raw).pathname.split('/').filter(Boolean)
    if (parts.length < 2) return ''
    const prefix = decodeURIComponent(parts[parts.length - 2] || '')
    const map = parsePrefixTenantMap()
    if (map && map[prefix]) return String(map[prefix]).trim()
    // Heurística: suffix TUC → tenant sin suffix (sanatoriomarcospazTUC → marcospaz)
    if (/TUC$/i.test(prefix)) {
      const guess = prefix.replace(/TUC$/i, '').replace(/sanatorio/i, '').trim()
      if (guess) return guess
    }
  } catch (_) {
    /* ignore */
  }
  return ''
}

/**
 * Hints tempranos desde URL (?tenant_id=) antes del ticket resolve.
 * @param {URLSearchParams|null|undefined} params
 * @param {string|null|undefined} [manifestUrl]
 */
export function applyUdvTelemetryHintsFromUrl(params, manifestUrl) {
  let tenantId = ''
  try {
    if (params) {
      const fromQuery = String(params.get('tenant_id') || params.get('tenantId') || '').trim()
      if (fromQuery) tenantId = fromQuery
    }
  } catch (_) {
    /* ignore */
  }
  if (!tenantId && manifestUrl) {
    tenantId = tenantIdFromManifestUrl(manifestUrl)
  }
  if (!tenantId && !session) return
  setUdvTelemetrySession({
    tenantId: tenantId || session?.tenantId,
    telemetryBaseUrl: session?.telemetryBaseUrl,
    telemetryToken: session?.telemetryToken,
    source: session?.source || 'url',
  })
}

/**
 * @param {string|null|undefined} manifestUrl
 */
export function mergeUdvTelemetryFromManifestUrl(manifestUrl) {
  if (!manifestUrl) return
  const inferred = tenantIdFromManifestUrl(manifestUrl)
  if (!inferred) return
  if (session?.tenantId && session.source === 'ticket') return
  setUdvTelemetrySession({
    tenantId: inferred,
    telemetryBaseUrl: session?.telemetryBaseUrl,
    telemetryToken: session?.telemetryToken,
    source: session?.tenantId ? session.source : 'manifest',
  })
}

function sanitizeTelemetryBaseUrl(baseUrl) {
  const b = String(baseUrl || '')
    .trim()
    .replace(/\/+$/, '')
  if (!b) return ''
  try {
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      /^http:\/\//i.test(b)
    ) {
      return ''
    }
  } catch (_) {
    /* ignore */
  }
  return b
}

/**
 * @param {{ tenant?: string, telemetryBaseUrl?: string, telemetryToken?: string }} [overrides]
 * @returns {{ enabled: boolean, tenant: string, baseUrl: string, token: string }}
 */
export function resolveUdvTelemetryTarget(overrides) {
  const o = overrides && typeof overrides === 'object' ? overrides : {}
  const tenant = String(
    o.tenant ||
      (session && session.tenantId) ||
      process.env.REACT_APP_UDV_TELEMETRY_TENANT ||
      ''
  ).trim()
  const baseUrl = sanitizeTelemetryBaseUrl(
    o.telemetryBaseUrl ||
      (session && session.telemetryBaseUrl) ||
      process.env.REACT_APP_UDV_TELEMETRY_URL ||
      ''
  )
  const token = String(
    o.telemetryToken ||
      (session && session.telemetryToken) ||
      process.env.REACT_APP_UDV_TELEMETRY_TOKEN ||
      ''
  ).trim()
  return {
    enabled: baseUrl.length > 0 && tenant.length > 0,
    tenant,
    baseUrl,
    token,
  }
}
